import { computed } from 'mobx';
import Model from 'core/model/Model';
import {
  PLATFORM_OPTIONS,
  PLATFORM_STATUS_HEALTH,
  PLATFORM_STATUS_HEALTH_LABELS,
  MITIGATION_TYPES,
  CFMT_AUTH_METHOD,
  A10_FLOW
} from '@kentik/ui-shared/mitigations/constants';
import MitigationMethodCollection from './MitigationMethodCollection';

export default class MitigationPlatformModel extends Model {
  get urlRoot() {
    return '/api/ui/mitigations/platforms';
  }

  get defaults() {
    return {
      name: '',
      description: '',
      mitigationMethods: new MitigationMethodCollection(),
      methodIds: []
    };
  }

  get messages() {
    return {
      create: `Mitigation platform "${this.get('name')}" was added successfully`,
      update: `Mitigation platform "${this.get('name')}" was updated successfully`,
      destroy: `Mitigation platform "${this.get('name')}" was removed successfully`
    };
  }

  @computed
  get methods() {
    return this.get('mitigationMethods');
  }

  @computed
  get methodIds() {
    return this.methods.map((method) => method.id);
  }

  @computed
  get methodCount() {
    return this.methods.size;
  }

  @computed
  get name() {
    return this.get('name') || '';
  }

  @computed
  get description() {
    return this.get('description') || '';
  }

  @computed
  get type() {
    return this.get('type') || '';
  }

  @computed
  get typeLabel() {
    return PLATFORM_OPTIONS.find((option) => this.type === option.value)?.label;
  }

  @computed
  get details() {
    return this.get(this.type) || {};
  }

  @computed
  get devices() {
    return this.details?.devices || [];
  }

  @computed
  get status() {
    return this.get('status') || {};
  }

  @computed
  get statusDetails() {
    return this.status[this.type] || {};
  }

  @computed
  get deprecatedAdaptiveFlowspecStatusDetails() {
    return this.status?.details || {};
  }

  @computed
  get statusHealth() {
    return this.status.health || PLATFORM_STATUS_HEALTH.UNKNOWN;
  }

  @computed
  get statusHealthLabel() {
    return PLATFORM_STATUS_HEALTH_LABELS[this.statusHealth];
  }

  @computed
  get statusHealthColor() {
    switch (this.statusHealth) {
      case PLATFORM_STATUS_HEALTH.WARNING:
        return 'warning';
      case PLATFORM_STATUS_HEALTH.FAILING:
        return 'danger';
      case PLATFORM_STATUS_HEALTH.HEALTHY:
        return 'success';
      default:
        return undefined;
    }
  }

  @computed
  get isHealthy() {
    return this.statusHealth === PLATFORM_STATUS_HEALTH.HEALTHY;
  }

  @computed
  get isFailing() {
    return this.statusHealth === PLATFORM_STATUS_HEALTH.FAILING;
  }

  @computed
  get isWarning() {
    return this.statusHealth === PLATFORM_STATUS_HEALTH.WARNING;
  }

  @computed
  get statusErrors() {
    return (this.status.errors || []).filter(Boolean);
  }

  serialize(data) {
    const methods = this.methods?.models || [];
    const methodIds = methods.map((model) => model.attributes.id);

    const dataToSerialize = {
      name: data.name,
      description: data.description,
      [data.type]: data[data.type],
      mitigationMethods: [...new Set(methodIds)] // dedupe IDs
    };

    if (data.type === MITIGATION_TYPES.RADWARE) {
      // if HA was turned off, clear secondary IP before saving, can't have one without the other
      if (!data[data.type].uiProperties?.enableHA) {
        data[data.type].secondaryIp = '';
      }

      // clean up UI only field values
      delete data[data.type].uiProperties;
    }

    if (data.type === MITIGATION_TYPES.CFMT) {
      const authMethod = data[data.type].uiProperties?.authMethod;

      // strip fields from the unused auth branch
      if (authMethod === CFMT_AUTH_METHOD.TOKEN) {
        delete data[data.type].apiLogin;
        delete data[data.type].apiPassword;
      } else {
        delete data[data.type].apiToken;
      }

      // clean up UI-only field
      delete data[data.type].uiProperties;
    }

    if (data.type === MITIGATION_TYPES.A10) {
      // absent or unspecified flow means direct-to-Thunder
      if (!data[data.type].flow || data[data.type].flow === A10_FLOW.UNSPECIFIED) {
        data[data.type].flow = A10_FLOW.THUNDER_DIRECT;
      }
      // login does not apply to the ADO flow
      if (data[data.type].flow === A10_FLOW.ADO) {
        delete data[data.type].login;
      }
    }

    // cleanup empty passwords
    if (data.type === MITIGATION_TYPES.RADWARE && data[data.type].password === '') {
      delete data[data.type].password;
    }
    if (data.type === MITIGATION_TYPES.CFMT && data[data.type].apiPassword === '') {
      delete data[data.type].apiPassword;
    }
    if (data.type === MITIGATION_TYPES.CFMT && data[data.type].apiToken === '') {
      delete data[data.type].apiToken;
    }
    if (data.type === MITIGATION_TYPES.A10 && data[data.type].password === '') {
      delete data[data.type].password;
    }

    return super.serialize(dataToSerialize);
  }

  deserialize(data) {
    const { mitigationMethods = [], ...restData } = data.platform || data;
    const type = PLATFORM_OPTIONS.find((option) => restData[option.value])?.value;

    if (type === MITIGATION_TYPES.RADWARE) {
      if (restData[type].secondaryIp) {
        restData[type].uiProperties = { enableHA: true };
      }
    }

    if (type === MITIGATION_TYPES.CFMT) {
      const authMethod = restData[type]?.apiLogin ? CFMT_AUTH_METHOD.KEY : CFMT_AUTH_METHOD.TOKEN;
      restData[type] = { ...restData[type], uiProperties: { authMethod } };
    }

    if (type === MITIGATION_TYPES.A10) {
      // absent or unspecified flow means direct-to-Thunder
      const { flow } = restData[type];
      restData[type] = {
        ...restData[type],
        flow: !flow || flow === A10_FLOW.UNSPECIFIED ? A10_FLOW.THUNDER_DIRECT : flow
      };
    }

    const deserializedData = Object.assign(this.defaults, {
      mitigationMethods: new MitigationMethodCollection(mitigationMethods),
      type,
      ...restData
    });

    return super.deserialize(deserializedData);
  }
}
